import React, { useEffect, useMemo, useState } from "react";
import BreadCumb from "../../components/common/Breadcrumb";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import backArrow from "../../assets/task/back_arrow.svg";
import DynamicTable from "../../components/common/DynamicTable";
import { SortingState } from "@tanstack/react-table";
import { TODO_HISTORY_QUERY, TODOS } from "../../api/query";
import { useLazyQuery, useMutation } from "@apollo/client";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { format, isBefore, parse, parseISO } from "date-fns";
import DateSelector from "../../components/common/DateSelector";
import { showError } from "../../components/common/ToastNotification";
import Loader from "../../components/common/Loader";
import { TODO_ACTIONS } from "../../api/mutation";

const employeeTaskSchema = yup.object().shape({
  start_date: yup.string().required("This field is required"),
  end_date: yup.string().required("This field is required"),
});

interface TodoRow {
  id: number;
  title: string;
  date: any;
  is_completed: string;
}

const AllTodo = () => {
  const navigate = useNavigate();
  const { userInfo } = useAuth();
  const employeeID = userInfo?.employee_id;

  const [sorting, setSorting] = useState<SortingState>([]);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const now = new Date();
  const currentDateAndTime = format(now, "yyyy-MM-dd'T'HH:mm:ssxxx").replace(
    /(\+|\-)\d{2}:\d{2}$/,
    "Z",
  );

  const {
    setValue,
    formState: { errors },
    watch,
    trigger,
    control,
  } = useForm<any>({
    resolver: yupResolver(employeeTaskSchema),
    defaultValues: {
      start_date: format(new Date(), "yyyy-MM-dd"),
      end_date: format(new Date(), "yyyy-MM-dd"),
    },
  });

  const watchStartDate = watch("start_date");
  const watchEndDate = watch("end_date");

  const [updateTodoStatus, { loading: updateToDoLoading }] = useMutation(
    TODO_ACTIONS,
    {
      refetchQueries: [TODOS], // optional: refresh list after update
      awaitRefetchQueries: true,
    },
  );

  const [getData, { data, loading }] = useLazyQuery(TODO_HISTORY_QUERY, {
    fetchPolicy: "no-cache",
  });

  const fetchTodos = (page: number = 1, pageSize: number = 400) => {
    if (!watchStartDate || !watchEndDate) {
      return;
    }

    getData({
      variables: {
        member_id: parseInt(employeeID),
        start_date: watchStartDate,
        end_date: watchEndDate,
      },
    });
  };

  useEffect(() => {
    fetchTodos();
  }, [employeeID, watchStartDate, watchEndDate]);

  useEffect(() => {
    if (data?.todo_history?.status_code === 200) {
      setTotalPages(data?.todo_history?.pagination?.total_pages || 0);
    }
  }, [data]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const formattedData: TodoRow[] = useMemo(() => {
    if (data?.todo_history?.status_code !== 200) {
      return [];
    }

    return (data?.todo_history?.todos || []).map(
      (todo: any): TodoRow => ({
        id: todo.id,
        title: todo.title ?? "",
        date: todo?.due_date
          ? format(new Date(todo.due_date), "MM-dd-yyyy")
          : "",
        is_completed: todo?.is_completed ? "Completed" : "Not Completed",
      }),
    );
  }, [data]);

  const paginationhandle = (pageNo: number, itemsPerPage: number) => {
    setCurrentPage(pageNo);
    fetchTodos(pageNo, itemsPerPage);
  };

  const columns = [
    {
      key: "title",
      name: "Title",
      id: "title",
      header: "Title",
      accessorKey: "title",
      enableSorting: true,
      sortingFn: (rowA: any, rowB: any, columnId: string) => {
        const valueA = String(rowA.getValue(columnId) || "").toLowerCase();

        const valueB = String(rowB.getValue(columnId) || "").toLowerCase();

        return valueA.localeCompare(valueB);
      },
    },
    {
      key: "date",
      name: "Due Date",
      id: "date",
      header: "Due Date",
      accessorKey: "date",
      enableSorting: true,
      sortingFn: (rowA: any, rowB: any) => {
        const dateA = parse(
          rowA.getValue("date") || "01-01-1970",
          "MM-dd-yyyy",
          new Date(),
        );

        const dateB = parse(
          rowB.getValue("date") || "01-01-1970",
          "MM-dd-yyyy",
          new Date(),
        );

        return dateA.getTime() - dateB.getTime();
      },
    },
    {
      key: "is_completed",
      name: "Status",
      id: "is_completed",
      header: "Status",
      accessorKey: "is_completed",
      enableSorting: true,
      sortingFn: (rowA: any, rowB: any, columnId: string) => {
        const valueA = String(rowA.getValue(columnId) || "").toLowerCase();

        const valueB = String(rowB.getValue(columnId) || "").toLowerCase();

        return valueA.localeCompare(valueB);
      },
    },
    {
      key: "action",
      name: "Action",
      id: "action",
      header: "Action",
      accessorKey: "action",
      enableSorting: true,
      cell: ({ row }: any) => {
        return (
          <div className="flex items-center gap-[8px] theme_btn_item">
            <button
              className="dash-action-link cursor-pointer"
              onClick={() => {
                navigate("/edit_todo", {
                  state: {
                    id: row.original.id,
                    viewOnly: true,
                  },
                });
              }}
            >
              <span className="text-sm dark:text-white">View</span>
            </button>
            {/* {row.original.is_completed == "Not Completed" && (
              <button
                className="dash-action-link cursor-pointer"
                onClick={() => {
                  updateTodoStatus({
                    variables: {
                      id: parseInt(row.original.id),
                      is_completed: true,
                      completed_date: currentDateAndTime,
                    },
                  });
                }}
              >
                <span className="text-sm dark:text-white">Resolve</span>
              </button>
            )} */}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div
        id="cts-respon-p-dv"
        className="w-full flex flex-col items-start justify-start py-40 px-20 mq575:px-5 box-border gap-[20px] max-w-[100%] mq1425:max-w-full"
      >
        <div className="w-full flex flex-col items-start justify-start gap-6">
          <BreadCumb />

          <div className="self-stretch flex flex-row items-center justify-start gap-[5px] text-2xl">
            <img
              className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img"
              alt="back"
              src={backArrow}
              onClick={() => navigate(-1)}
            />

            <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit inline-block max-w-[calc(100%_-_29px)] mq450:text-base dark:text-white">
              All To Dos
            </h3>
          </div>

          <div className="m-0 self-stretch rounded-radius-radius-lg bg-surface-bg-blue darkborder-b-remove flex flex-col items-start justify-start p-8 mq575:p-3 box-border gap-spacing-lg max-w-full mq825:pt-5 mq825:pb-5 mq825:box-border mq1425:pt-[21px] mq1425:pb-[21px] mq1425:box-border">
            <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
              <div className="status-filter pb-4 grid gap-[8px]">
                <Controller
                  name="start_date"
                  control={control}
                  render={({ field }) => (
                    <DateSelector
                      label="Start Date*"
                      selectedDate={field.value}
                      onChange={(date: string | any) => {
                        const endDate = watch("end_date");
                        const result = true;
                        if (result) {
                          field.onChange(date);
                          setValue("end_date", null);
                          setValue("start_date", date);
                          trigger("start_date");
                          localStorage.setItem(
                            "employeeTaskFilterStartDate",
                            date,
                          );
                        }
                      }}
                      nothideParsedValue={true}
                      blueIcon={true}
                      error={errors.start_date?.message}
                      showAllDate={true}
                    />
                  )}
                />
              </div>
              <div className="status-filter pb-4 grid gap-[8px]">
                <Controller
                  name="end_date"
                  control={control}
                  render={({ field }) => (
                    <DateSelector
                      label="End Date*"
                      selectedDate={field.value}
                      onChange={(date: string | null | any) => {
                        const date2 = parseISO(watch("start_date"));
                        const date1 = parseISO(date);
                        if (isBefore(date1, date2)) {
                          showError(
                            "Please select date greater than start date.",
                          );
                        } else {
                          field.onChange(date);
                          setValue("end_date", date);
                          trigger("end_date");
                          localStorage.setItem(
                            "employeeTaskFilterEndDate",
                            date,
                          );
                        }
                      }}
                      nothideParsedValue={true}
                      blueIcon={true}
                      dontopenDatepicker={!watch("start_date")}
                      error={errors.end_date?.message}
                      showAllDate={true}
                    />
                  )}
                />
              </div>
            </div>

            {formattedData.length > 0 ? (
              <div className="w-full emptasklist-tbl">
                <DynamicTable
                  filteredData={formattedData}
                  selectedColumns={columns}
                  sorting={sorting}
                  setSorting={setSorting}
                  selectedRows={selectedRows}
                  setSelectedRows={setSelectedRows}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  handlePageChange={paginationhandle}
                  showPagination={true}
                  paginationDataLength={totalPages > 1 || currentPage !== 1}
                />
              </div>
            ) : (
              <div className="self-stretch rounded-lg overflow-hidden flex flex-col items-center justify-center py-[20px] px-[20px] gap-[8px] text-center text-text-text-secondary">
                <div className="self-stretch bg-white rounded-lg bg-slate-100 dark:bg-slate-700 flex flex-col items-center justify-center py-[110px] px-[53px] gap-[8px]">
                  <p className="relative my-0">
                    {loading ? "Loading..." : "No data available"}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {(loading || updateToDoLoading) && <Loader />}
    </>
  );
};

export default AllTodo;
